/**
 * LabelPreview — the live, FDA-rounded Nutrition Facts panel.
 *
 * This is the heart of the editor's first-value moment: the merchant types numbers and the
 * compliant panel assembles in real time. It renders the SAME SVG the server export will
 * rasterize (`renderLabelSVG` from `@easynutrifact/label-renderer`), so preview == export by
 * construction.
 *
 * CLIENT-SIDE BUNDLING: `renderLabelSVG` is imported from the renderer's `./client` subpath,
 * which resolves to the pure `svg-us-nf.ts` module. The package's index re-exports node-only
 * font-path helpers (`node:url`); importing the index here would pull `node:url` into the
 * browser bundle and fail the Vite build. The `./client` entry is browser-safe by construction.
 *
 * The SVG string is injected inline via `dangerouslySetInnerHTML`. The renderer already
 * XML-escapes every text/attribute value it emits (see svg-us-nf.ts `esc()`), and the only
 * inputs are the engine-built PanelModel (no raw HTML, no script). As defense-in-depth against
 * any future renderer regression, `sanitizeSvg()` parses the string in the browser and strips
 * script/foreignObject elements plus event-handler and javascript: attributes before injection
 * — so the injected markup is provably inert without adding a sanitizer dependency. We style the
 * inner svg to scale responsively to the panel column width.
 *
 * The panel itself uses font-family="Liberation Sans"; in the browser the metric-compatible
 * system sans (Arial/Helvetica) fallback is used for M1 (near-identical layout).
 * M-Launch: @font-face Liberation Sans for pixel-exact preview==export when the export route ships.
 */
import { useEffect, useMemo, useState } from "react";
import type { PanelModel } from "@easynutrifact/label-engine";
import { renderLabelSVG } from "@easynutrifact/label-renderer/client";
import { useT } from "../i18n/context";

export interface LabelPreviewProps {
  /** The assembled panel to render, or null when there isn't enough data yet. */
  panel: PanelModel | null;
}

export function LabelPreview({ panel }: LabelPreviewProps) {
  const t = useT();
  // The specimen only paints in the browser: sanitizeSvg produces DIFFERENT output
  // on the server (regex fallback, no DOMParser) vs the client (DOMParser parse +
  // re-serialize normalizes attributes/whitespace/self-closing tags), so rendering
  // the SVG during SSR caused a hydration mismatch (React #418/#423/#425, ~14 per
  // load — one per differing SVG node) on /app/labels/:id (found by the A2 reviewer
  // walk, 100% reproducible). Gate it to after mount: SSR + the first client render
  // both show the empty specimen (so hydration matches exactly), then the live
  // preview paints post-hydration. Re-render only when the panel content changes.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const svg = useMemo(
    () =>
      mounted && panel
        ? sanitizeSvg(renderLabelSVG(panel, "vertical-us-nf"))
        : null,
    [mounted, panel],
  );

  return (
    <div className="lf-preview" aria-live="polite">
      <div className="lf-preview__surface">
        {svg ? (
          // The specimen: the live FDA panel on a paper surface.
          <div
            className="lf-preview__specimen"
            role="img"
            aria-label={t("labelPreview.ariaLiveLabel")}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : (
          <EmptyPreview />
        )}
      </div>
      {svg ? <p className="lf-preview__caption">{t("shared.livePreviewCaption")}</p> : null}

      {/* Component-scoped styles. Polaris WC doesn't own this internal canvas, so a small
          style block here keeps the specimen framing self-contained and reviewer-safe
          (no global CSS, no Polaris token overrides). */}
      <style dangerouslySetInnerHTML={{ __html: PREVIEW_STYLES }} />
    </div>
  );
}

/**
 * Strip any potentially-active content from an SVG string before it's injected.
 * The renderer never emits these, but this makes the injection inert by construction
 * regardless of future renderer changes — without pulling in a sanitizer dependency.
 *
 * Browser path: parse with DOMParser, remove <script>/<foreignObject>/<a> and any attribute
 * that is an event handler (on*) or a javascript:/data: URL, then re-serialize.
 * SSR/no-DOM path: a conservative regex fallback (this path effectively never runs because the
 * specimen only paints in the browser, but it keeps the function total and safe).
 */
export function sanitizeSvg(svg: string): string {
  if (typeof DOMParser === "undefined") {
    return svg
      .replace(/<\s*(script|foreignObject)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
      .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
      .replace(/(href|xlink:href)\s*=\s*("\s*javascript:[^"]*"|'\s*javascript:[^']*')/gi, "");
  }
  const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  const root = doc.documentElement;
  // If parsing failed (parsererror root), return an empty string rather than risky markup.
  if (!root || root.nodeName.toLowerCase() === "parsererror") return "";

  root.querySelectorAll("script, foreignObject, a").forEach((el) => el.remove());
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  const toScrub: Element[] = [root];
  while (walker.nextNode()) toScrub.push(walker.currentNode as Element);
  for (const el of toScrub) {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.replace(/\s+/g, "").toLowerCase();
      if (name.startsWith("on") || value.startsWith("javascript:")) {
        el.removeAttribute(attr.name);
      }
    }
  }
  return new XMLSerializer().serializeToString(root);
}

/** Warm empty state — invites the merchant to start, and points at where the panel will appear. */
function EmptyPreview() {
  const t = useT();
  return (
    <div className="lf-preview__empty">
      <div className="lf-preview__empty-mark" aria-hidden="true">
        {/* A faint outline of a Nutrition Facts panel — the shape that's about to fill in. */}
        <svg viewBox="0 0 120 150" width="96" height="120" fill="none">
          <rect x="2" y="2" width="116" height="146" rx="3" stroke="currentColor" strokeWidth="2" />
          <line x1="10" y1="26" x2="110" y2="26" stroke="currentColor" strokeWidth="5" />
          <line x1="10" y1="40" x2="110" y2="40" stroke="currentColor" strokeWidth="2" />
          <line x1="10" y1="58" x2="80" y2="58" stroke="currentColor" strokeWidth="1" />
          <line x1="10" y1="72" x2="90" y2="72" stroke="currentColor" strokeWidth="1" />
          <line x1="10" y1="86" x2="70" y2="86" stroke="currentColor" strokeWidth="1" />
          <line x1="10" y1="100" x2="95" y2="100" stroke="currentColor" strokeWidth="1" />
          <line x1="10" y1="118" x2="110" y2="118" stroke="currentColor" strokeWidth="3" />
          <line x1="10" y1="132" x2="60" y2="132" stroke="currentColor" strokeWidth="1" />
        </svg>
      </div>
      <p className="lf-preview__empty-title">{t("labelPreview.emptyTitle")}</p>
      <p className="lf-preview__empty-body">{t("labelPreview.emptyBody")}</p>
    </div>
  );
}

const PREVIEW_STYLES = `
.lf-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.lf-preview__surface {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 28px 20px;
  border-radius: 14px;
  background:
    radial-gradient(120% 80% at 50% 0%, rgba(0,0,0,0.04), transparent 60%),
    repeating-linear-gradient(45deg, rgba(0,0,0,0.012) 0 12px, transparent 12px 24px),
    #f6f6f4;
  border: 1px solid rgba(0,0,0,0.08);
  min-height: 320px;
}
.lf-preview__specimen {
  background: #ffffff;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.10), 0 8px 24px rgba(0,0,0,0.10);
  max-width: 280px;
  width: 100%;
}
.lf-preview__specimen svg {
  display: block;
  width: 100%;
  height: auto;
}
.lf-preview__caption {
  margin: 0;
  text-align: center;
  font-size: 12px;
  letter-spacing: 0.02em;
  color: rgba(0,0,0,0.52);
}
.lf-preview__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  max-width: 320px;
  padding: 12px;
}
.lf-preview__empty-mark { color: rgba(0,0,0,0.18); }
.lf-preview__empty-title {
  margin: 4px 0 0;
  font-weight: 650;
  font-size: 15px;
  color: rgba(0,0,0,0.78);
}
.lf-preview__empty-body {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(0,0,0,0.55);
}
@media (prefers-color-scheme: dark) {
  .lf-preview__surface {
    background:
      radial-gradient(120% 80% at 50% 0%, rgba(255,255,255,0.05), transparent 60%),
      #1f1f22;
    border-color: rgba(255,255,255,0.10);
  }
  .lf-preview__caption { color: rgba(255,255,255,0.55); }
  .lf-preview__empty-mark { color: rgba(255,255,255,0.22); }
  .lf-preview__empty-title { color: rgba(255,255,255,0.85); }
  .lf-preview__empty-body { color: rgba(255,255,255,0.6); }
  /* The specimen stays white — a Nutrition Facts panel is always black-on-white by regulation. */
}
`;
